import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "./Logo";

/* Shared dark marketing background — identical look to Landing. */
function MarketingBackground() {
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-[#05060f]" />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage: "radial-gradient(ellipse 90% 70% at 50% 0%, black 20%, transparent 70%)",
        }}
      />
      <div className="absolute -top-[300px] left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-blue-600/15 blur-[120px]" />
    </div>
  );
}

function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3 bg-[#05060f]/70 backdrop-blur-xl border-b border-white/[0.06]" : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <Link to="/" className="flex items-center gap-2.5 justify-self-start" data-testid="nav-logo">
          <Logo size={32} glow />
          <span className="text-white font-semibold tracking-tight text-base" style={{ fontFamily: "Manrope" }}>
            HeDesk
          </span>
        </Link>
        <nav className="hidden md:flex items-center justify-center gap-8 justify-self-center">
          <Link to="/#features" className="text-sm text-slate-300 hover:text-white">Features</Link>
          <Link to="/#pricing" className="text-sm text-slate-300 hover:text-white">Pricing</Link>
          <Link to="/blog" className="text-sm text-slate-300 hover:text-white">Blog</Link>
        </nav>
        <div className="justify-self-end flex items-center gap-3">
          <Link to="/login" className="hidden sm:inline text-sm text-slate-300 hover:text-white">Log in</Link>
          <Link to="/signup">
            <button className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-white/[0.08] border border-white/[0.12] hover:bg-white/[0.14] transition-all">
              Get started
            </button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function MarketingFooter() {
  return (
    <footer className="relative border-t border-white/10 bg-white/[0.02] mt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
        <div className="flex items-center gap-2">
          <Logo size={20} />
          <span>© {new Date().getFullYear()} HeDesk · Made in India for Indian clinics</span>
        </div>
        <nav className="flex items-center gap-5">
          <Link to="/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/terms" className="hover:text-white">Terms</Link>
          <Link to="/blog" className="hover:text-white">Blog</Link>
          <a href="mailto:hello@hedesk.help" className="hover:text-white">Contact</a>
        </nav>
      </div>
    </footer>
  );
}

/**
 * Wrapper for static marketing pages (Privacy, Terms, Blog post body).
 * Provides nav + footer + background + container.
 */
export default function MarketingShell({ children, narrow = true }) {
  return (
    <div className="min-h-screen text-white relative">
      <MarketingBackground />
      <MarketingNav />
      <main className={`pt-32 sm:pt-36 pb-16 px-4 sm:px-6`}>
        <div className={`${narrow ? "max-w-3xl" : "max-w-5xl"} mx-auto`}>{children}</div>
      </main>
      <MarketingFooter />
    </div>
  );
}
